import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { productionTestAPI } from '../services/api';

const STATUS_COLORS = {
  healthy: '#27ae60',
  degraded: '#f39c12',
  broken: '#e74c3c',
  error: '#95a5a6'
};

const STATUS_BG = {
  healthy: '#d4edda',
  degraded: '#fff3cd',
  broken: '#f8d7da',
  error: '#e2e3e5'
};

function MetricCard({ label, value, color }) {
  return (
    <div style={{
      background: 'white',
      border: '1px solid #e0e0e0',
      borderRadius: '8px',
      padding: '1rem 1.5rem',
      textAlign: 'center'
    }}>
      <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '0.25rem' }}>{label}</div>
      <div style={{ fontSize: '1.6rem', fontWeight: 700, color: color || '#2c3e50' }}>{value}</div>
    </div>
  );
}

function OutputWindow({ result, onClose }) {
  if (!result) return null;
  const m = result.metrics || {};
  const lat = m.latency_metrics || {};
  const statusColor = STATUS_COLORS[result.status] || '#95a5a6';
  const statusBg = STATUS_BG[result.status] || '#e2e3e5';

  return (
    <div style={{
      border: `2px solid ${statusColor}`,
      borderRadius: '8px',
      padding: '1.5rem',
      background: statusBg,
      marginBottom: '1.5rem'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span style={{
            display: 'inline-block',
            padding: '0.3rem 0.8rem',
            borderRadius: '16px',
            fontWeight: 700,
            fontSize: '1rem',
            background: statusColor,
            color: 'white'
          }}>
            {result.status?.toUpperCase()}
          </span>
          <span style={{ color: '#555', fontSize: '0.9rem' }}>
            {new Date(result.createdAt).toLocaleString()}
          </span>
        </div>
        {onClose && (
          <button onClick={onClose} className="btn btn-secondary" style={{ padding: '0.3rem 0.8rem', fontSize: '0.85rem' }}>
            Dismiss
          </button>
        )}
      </div>

      {result.status === 'error' ? (
        <div className="alert alert-danger" style={{ margin: 0 }}>
          {result.rawResponse?.error || 'Unknown error'}
        </div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
            <MetricCard label="Total Requests" value={m.total_requests ?? '—'} />
            <MetricCard
              label="Successful"
              value={m.successful_requests ?? '—'}
              color="#27ae60"
            />
            <MetricCard
              label="Failed"
              value={m.failed_requests ?? '—'}
              color={m.failed_requests > 0 ? '#e74c3c' : undefined}
            />
            <MetricCard
              label="Success Rate"
              value={m.success_rate != null ? `${m.success_rate}%` : '—'}
              color={m.success_rate != null ? (m.success_rate >= 90 ? '#27ae60' : '#e74c3c') : undefined}
            />
            <MetricCard
              label="Avg Latency"
              value={lat.avg != null ? `${lat.avg}ms` : '—'}
              color={lat.avg > 30000 ? '#f39c12' : undefined}
            />
            <MetricCard label="P95 Latency" value={lat.p95 != null ? `${lat.p95}ms` : '—'} />
            <MetricCard label="P99 Latency" value={lat.p99 != null ? `${lat.p99}ms` : '—'} />
            {m.execution_time_ms != null && (
              <MetricCard label="Exec Time" value={`${(m.execution_time_ms / 1000).toFixed(1)}s`} />
            )}
          </div>

          {m.status_distribution && Object.keys(m.status_distribution).length > 0 && (
            <div style={{ fontSize: '0.85rem', color: '#555' }}>
              <strong>Status codes:</strong>{' '}
              {Object.entries(m.status_distribution).map(([code, count]) => (
                <span key={code} style={{ marginRight: '1rem' }}>
                  HTTP {code}: <strong>{count}</strong>
                </span>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ProductionTestDetail() {
  const { id } = useParams();
  const [test, setTest] = useState(null);
  const [results, setResults] = useState([]);
  const [loadingTest, setLoadingTest] = useState(true);
  const [loadingResults, setLoadingResults] = useState(true);
  const [running, setRunning] = useState(false);
  const [latestOutput, setLatestOutput] = useState(null);
  const [error, setError] = useState(null);

  const fetchTest = useCallback(async () => {
    try {
      const res = await productionTestAPI.getById(id);
      setTest(res.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingTest(false);
    }
  }, [id]);

  const fetchResults = useCallback(async () => {
    try {
      const res = await productionTestAPI.getResults(id);
      setResults(res.data);
    } catch (err) {
      console.error('Failed to fetch results:', err);
    } finally {
      setLoadingResults(false);
    }
  }, [id]);

  useEffect(() => {
    fetchTest();
    fetchResults();
  }, [fetchTest, fetchResults]);

  const handleRun = async () => {
    setRunning(true);
    setLatestOutput(null);
    setError(null);
    try {
      const res = await productionTestAPI.run(id);
      // Build a display-compatible result object from the run response
      setLatestOutput({
        status: res.data.status,
        metrics: res.data.metrics,
        createdAt: res.data.createdAt || new Date().toISOString(),
        rawResponse: {}
      });
      await fetchResults();
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      setError(msg);
      setLatestOutput({ status: 'error', rawResponse: { error: msg }, createdAt: new Date().toISOString() });
    } finally {
      setRunning(false);
    }
  };

  const handleDeleteResult = async (resultId) => {
    if (!window.confirm('Delete this result?')) return;
    try {
      await productionTestAPI.deleteResult(id, resultId);
      setResults(prev => prev.filter(r => r._id !== resultId));
    } catch (err) {
      alert('Delete failed: ' + err.message);
    }
  };

  if (loadingTest) return <div className="loading"><div className="spinner"></div></div>;
  if (!test) return <div className="card"><div className="alert alert-danger">{error || 'Test not found'}</div></div>;

  const authSummary = test.auth?.type === 'none' || !test.auth?.type
    ? 'None'
    : test.auth.type === 'api_key'
      ? `API Key (${test.auth.key_name})`
      : test.auth.type === 'bearer'
        ? 'Bearer Token'
        : 'Basic Auth';

  return (
    <div>
      {/* Header card */}
      <div className="card">
        <div className="flex-between mb-3">
          <h2>{test.name}</h2>
          <div className="flex" style={{ gap: '0.5rem' }}>
            <button
              onClick={handleRun}
              disabled={running}
              className="btn btn-success"
            >
              {running ? 'Running…' : 'Run Tests'}
            </button>
            <Link to={`/production-tests/test/${id}/edit`} className="btn btn-secondary">Edit</Link>
            <Link to="/production-tests" className="btn btn-secondary">Back</Link>
          </div>
        </div>

        {test.description && (
          <p style={{ color: '#555', marginBottom: '1rem' }}>{test.description}</p>
        )}

        <div className="grid grid-3" style={{ marginBottom: '0' }}>
          <div>
            <strong>Endpoint:</strong>
            <div><code style={{ fontSize: '0.85rem', wordBreak: 'break-all' }}>{test.endpoint}</code></div>
          </div>
          <div>
            <strong>Method / Auth:</strong>
            <div>{test.method} · {authSummary}</div>
          </div>
          <div>
            <strong>Config:</strong>
            <div>
              Concurrency: {test.concurrency} · Timeout: {test.timeout}s ·
              Success: [{test.success_codes?.join(', ')}]
            </div>
          </div>
        </div>
      </div>

      {/* Running indicator */}
      {running && (
        <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
          <div className="spinner" style={{ margin: '0 auto 1rem' }}></div>
          <p style={{ color: '#555' }}>
            Running {test.test_cases?.length} test{test.test_cases?.length !== 1 ? 's' : ''} with concurrency {test.concurrency}…
          </p>
        </div>
      )}

      {/* Latest output window */}
      {latestOutput && !running && (
        <div className="card">
          <h3 style={{ marginBottom: '1rem' }}>Latest Result</h3>
          <OutputWindow result={latestOutput} onClose={() => setLatestOutput(null)} />
        </div>
      )}

      {/* Test cases summary */}
      <div className="card">
        <h3>Test Cases ({test.test_cases?.length || 0})</h3>
        <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
          <table style={{ fontSize: '0.875rem' }}>
            <thead>
              <tr>
                <th>#</th>
                <th>ID</th>
                <th>Query Params</th>
              </tr>
            </thead>
            <tbody>
              {(test.test_cases || []).map((tc, i) => (
                <tr key={tc._id || i}>
                  <td style={{ color: '#999' }}>{i + 1}</td>
                  <td><code>{tc.id}</code></td>
                  <td>
                    <code style={{ fontSize: '0.8rem' }}>
                      {JSON.stringify(tc.query_params)}
                    </code>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Results history */}
      <div className="card">
        <h3>Run History</h3>
        {loadingResults ? (
          <div className="loading" style={{ padding: '1rem 0' }}>
            <div className="spinner"></div>
          </div>
        ) : results.length === 0 ? (
          <p style={{ color: '#777' }}>No runs yet. Click "Run Tests" to execute.</p>
        ) : (
          <div>
            {results.map((r, idx) => (
              <div
                key={r._id}
                style={{
                  border: `1px solid ${STATUS_COLORS[r.status] || '#ddd'}`,
                  borderRadius: '6px',
                  marginBottom: '0.75rem',
                  overflow: 'hidden'
                }}
              >
                {/* Row header */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0.6rem 1rem',
                    background: STATUS_BG[r.status] || '#f8f9fa',
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    const el = document.getElementById(`result-body-${r._id}`);
                    if (el) el.style.display = el.style.display === 'none' ? 'block' : 'none';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <span style={{
                      display: 'inline-block',
                      padding: '0.2rem 0.6rem',
                      borderRadius: '12px',
                      fontSize: '0.78rem',
                      fontWeight: 700,
                      background: STATUS_COLORS[r.status] || '#95a5a6',
                      color: 'white'
                    }}>
                      {r.status?.toUpperCase()}
                    </span>
                    <span style={{ fontSize: '0.85rem', color: '#555' }}>
                      {new Date(r.createdAt).toLocaleString()}
                    </span>
                    {idx === 0 && (
                      <span style={{ fontSize: '0.75rem', color: '#3498db', fontWeight: 600 }}>LATEST</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', fontSize: '0.85rem' }}>
                    {r.metrics && (
                      <>
                        <span>
                          Success: <strong style={{ color: r.metrics.success_rate >= 95 ? '#27ae60' : '#e74c3c' }}>
                            {r.metrics.success_rate}%
                          </strong>
                        </span>
                        <span>Failed: <strong>{r.metrics.failed_requests}</strong></span>
                        <span>P95: <strong>{r.metrics.latency_metrics?.p95}ms</strong></span>
                        <span>Total: <strong>{r.metrics.total_requests}</strong></span>
                      </>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteResult(r._id); }}
                      className="btn btn-danger"
                      style={{ fontSize: '0.75rem', padding: '0.2rem 0.6rem' }}
                    >
                      Delete
                    </button>
                  </div>
                </div>

                {/* Expandable body */}
                <div id={`result-body-${r._id}`} style={{ display: 'none' }}>
                  <OutputWindow result={r} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ProductionTestDetail;
